import React, { useEffect, useState, useCallback } from 'react';
import {
  List,
  Col,
  Typography,
  Steps,
  message,
  Row,
  Divider,
  Skeleton,
  Grid,
} from 'antd';
import {
  GetStudyYearById,
  SignRegistrationByOrganization,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import type {
  OrganizationRegistration,
  ProcessSteps,
  StudyYear,
} from '../../Types/interfaces';
import { StepStatus } from '../../Types/interfaces';
import FileUploader from '../FileUploader';
import { formatDate } from '../../Utils/date';
import {
  collaborationDocumentStatus,
  renderStatus,
} from '../../Utils/organizationProcessUtils';

const { useBreakpoint } = Grid;

interface OrganizationElementComponentProps {
  organizationRegistration: OrganizationRegistration | undefined;
  isSignable?: boolean;
  isArchived: boolean;
  isUniversityOrganization: boolean;
}

const OrganizationElementComponent = ({
  organizationRegistration,
  isArchived,
  isUniversityOrganization,
}: OrganizationElementComponentProps): React.ReactNode => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [isSignatureDisabled, setIsSignatureDisabled] = useState(false);
  const [processSteps, setProcessSteps] = useState<ProcessSteps[]>([]);
  const [studyYear, setStudyYear] = useState<StudyYear>();
  const screens = useBreakpoint();

  const getSignatureState = (processSteps: ProcessSteps[]): void => {
    const step = processSteps.find(
      (e) =>
        e.stepName === 'SigningOrganizationRegistrationDocumentByOrganization'
    );

    switch (step?.stepStatus) {
      case 0:
        setIsSignatureDisabled(true);
        break;
      case 1:
        setIsSignatureDisabled(false);
        break;
      case 2:
        setIsSignatureDisabled(false);
        break;
      case 3:
        setIsSignatureDisabled(true);
        break;
      default:
        setIsSignatureDisabled(true);
    }
  };

  const fetchData = useCallback(async () => {
    if (!organizationRegistration) {
      return;
    }
    try {
      setProcessSteps(organizationRegistration.processSteps);
      getSignatureState(organizationRegistration.processSteps);

      const studyYearResp = await GetStudyYearById(
        organizationRegistration.collaborationDocument.studyYearId.toString()
      );
      setStudyYear(studyYearResp);
    } catch (error) {
      console.error(error);
    }
  }, [organizationRegistration]);

  useEffect(() => {
    const initFetch = async (): Promise<void> => {
      fetchData();
    };
    initFetch();
  }, [fetchData]);

  const signByOrganizationManager = async (
    registrationId: number
  ): Promise<void> => {
    if (!organizationRegistration) {
      return;
    }

    try {
      await SignRegistrationByOrganization(
        registrationId,
        organizationRegistration.organizationId
      );

      setProcessSteps(organizationRegistration.processSteps);
      getSignatureState(organizationRegistration.processSteps);
      const studyYearResp = await GetStudyYearById(
        organizationRegistration.collaborationDocument.studyYearId.toString()
      );

      setStudyYear(studyYearResp);

      messageApi.success(t('orgElementComp.signSuccess'));
    } catch (error) {
      console.log(error);
      messageApi.error(t('orgElementComp.signError'));
    }
  };

  if (organizationRegistration && studyYear) {
    return (
      <>
        {contextHolder}
        <List.Item>
          <div style={{ width: '100%', margin: '15px' }}>
            <Row gutter={[16, 8]} align="middle">
              <Col flex="200px">
                <Typography.Text>
                  {formatDate(studyYear.startDate) +
                    ' - ' +
                    formatDate(studyYear.endDate)}
                </Typography.Text>
              </Col>
              <Col flex="auto">
                <Steps
                  size="small"
                  direction={screens.xl ? 'horizontal' : 'vertical'}
                  items={[
                    {
                      title: (
                        <span
                          style={{
                            whiteSpace: 'normal',
                          }}
                        >
                          {t('orgElementComp.registered')}
                        </span>
                      ),
                      status: renderStatus(
                        processSteps,
                        'OrganizationRegistrationRequested'
                      ),
                    },
                    {
                      title: (
                        <span
                          style={{
                            whiteSpace: 'normal',
                          }}
                        >
                          {t('orgElementComp.legalRegistration')}
                        </span>
                      ),
                      status: renderStatus(
                        processSteps,
                        'RegisteringCollaborationDocument'
                      ),
                    },
                    {
                      title: (
                        <span
                          style={{
                            whiteSpace: 'normal',
                          }}
                        >
                          {t('orgElementComp.signedByManager')}
                        </span>
                      ),
                      status: renderStatus(
                        processSteps,
                        'SigningOrganizationRegistrationDocumentByOrganization'
                      ),
                    },
                    {
                      title: (
                        <span
                          style={{
                            whiteSpace: 'normal',
                          }}
                        >
                          {t('orgElementComp.signedByLegDep')}
                        </span>
                      ),
                      status: renderStatus(
                        processSteps,
                        'SigningOrganizationRegistrationDocumentByLegalDepartment'
                      ),
                    },
                    {
                      title: (
                        <span
                          style={{
                            whiteSpace: 'normal',
                          }}
                        >
                          {t('orgElementComp.signedByRector')},
                        </span>
                      ),
                      status: renderStatus(
                        processSteps,
                        'SigningCollaborationDocumentByRector'
                      ),
                    },
                  ]}
                />
                <Typography.Link
                  href={`${organizationRegistration.collaborationDocument.url}?status=${collaborationDocumentStatus(
                    processSteps
                  )}`}
                  target="_blank"
                >
                  {t('orgElementComp.viewDoc')}
                </Typography.Link>
              </Col>
            </Row>
            {!isArchived && (
              <div>
                <Divider />

                <FileUploader
                  apiUrl={'collaboration-documents'}
                  documentUrl={
                    organizationRegistration.collaborationDocument.url
                  }
                  documentSigningStatus={collaborationDocumentStatus(
                    processSteps
                  )}
                  isSignatureDisabled={isSignatureDisabled}
                  alreadySigned={
                    processSteps.find(
                      (e) => e.stepName === 'SigningWorkReportByTutor'
                    )?.stepStatus === StepStatus.updated
                  }
                  onFileUpload={async () => {
                    await signByOrganizationManager(
                      organizationRegistration.id
                    );
                  }}
                />
              </div>
            )}
          </div>
        </List.Item>
      </>
    );
  } else if (isUniversityOrganization) {
    if (isUniversityOrganization && organizationRegistration) {
      return (
        <List.Item>
          <div style={{ width: '100%' }}>
            <Row gutter={[16, 8]} align="middle">
              <Col flex="200px">
                <Typography.Text>
                  {organizationRegistration.organizationName}
                </Typography.Text>
              </Col>
            </Row>
          </div>
        </List.Item>
      );
    }
  } else {
    return (
      <List.Item>
        <div style={{ width: '100%', padding: '16px 0' }}>
          <Skeleton active />
        </div>
      </List.Item>
    );
  }
};

export default OrganizationElementComponent;
